import type { IChannel } from "containers/Channels/types";
import { ChannelType } from "store/types";

import type { TImportAcceptedKeys, TImportImmaterielKeys, TImportTitles } from "./import_data";
import { suggestColumnMapping } from "./suggestColumnMapping";
import type { TObj } from "./type";

export type ResolvedColumnMapping = {
    byApiKey: Record<string, string>;
    byLabel: Record<string, string>;
};

const resolveImmaterielBaseColumn = ({
    alternateKeys,
    fileTitles,
    channelType,
}: {
    alternateKeys: string[] | undefined;
    fileTitles: string[];
    channelType?: IChannel["type"];
}): string | undefined => {
    if (!alternateKeys?.length || !fileTitles.some((title) => alternateKeys.includes(title))) {
        return undefined;
    }

    return channelType == ChannelType.AMOUNT_BASED ? alternateKeys[0] : alternateKeys[1];
};

const crealoKeyForLabel = (importTitles: TImportTitles, columnTitle: string) =>
    Object.keys(importTitles).find((key) => importTitles[key] === columnTitle);

export const resolveColumnMapping = ({
    importTitles,
    importAcceptedKeys,
    fileTitles,
    savedPreferences,
    isImmateriel,
    channelType,
    mandatoryKeys,
}: {
    importTitles: TImportTitles;
    importAcceptedKeys: TImportAcceptedKeys | TImportImmaterielKeys;
    fileTitles: string[];
    savedPreferences?: Record<string, string> | false;
    isImmateriel: boolean;
    channelType?: IChannel["type"];
    /** When set, unmatched optional fields are preset to NotMandatory. Unmatched mandatory fields stay empty. */
    mandatoryKeys?: readonly string[];
}): ResolvedColumnMapping => {
    const byApiKey: Record<string, string> = {};
    const byLabel: Record<string, string> = {};
    const savedMapping: Record<string, string> = {};
    const blockedKeys = new Set<string>();

    if (savedPreferences) {
        for (const [columnTitle, value] of Object.entries(savedPreferences)) {
            const columnKey = crealoKeyForLabel(importTitles, columnTitle);
            if (!columnKey) continue;

            if (value === "NotMandatory") {
                blockedKeys.add(columnKey);
                byApiKey[columnKey] = value;
                byLabel[columnTitle] = value;
                continue;
            }

            if (!fileTitles.includes(value)) continue;
            savedMapping[value] = columnKey;
        }
    }

    if (isImmateriel) {
        for (const titleKey of Object.keys(importTitles)) {
            if (importTitles[titleKey] !== "CA/Quantité" || blockedKeys.has(titleKey)) continue;

            const fileColumn = resolveImmaterielBaseColumn({
                alternateKeys: importAcceptedKeys[titleKey],
                fileTitles,
                channelType,
            });
            if (!fileColumn || !fileTitles.includes(fileColumn)) continue;
            savedMapping[fileColumn] = titleKey;
        }
    }

    const crealoKeys = Object.fromEntries(
        Object.entries(importTitles).filter(([titleKey]) => !blockedKeys.has(titleKey)),
    );

    const suggested = suggestColumnMapping({
        fileTitles,
        crealoKeys,
        acceptedKeys: importAcceptedKeys,
        savedMapping,
    });

    for (const [fileColumn, crealoKey] of Object.entries(suggested)) {
        if (crealoKey === "Ignore" || blockedKeys.has(crealoKey)) continue;
        byApiKey[crealoKey] = fileColumn;
        byLabel[importTitles[crealoKey]] = fileColumn;
    }

    if (mandatoryKeys) {
        const mandatory = new Set(mandatoryKeys);
        for (const [apiKey, label] of Object.entries(importTitles)) {
            if (byApiKey[apiKey] || mandatory.has(apiKey)) continue;
            byApiKey[apiKey] = "NotMandatory";
            byLabel[label] = "NotMandatory";
        }
    }

    return { byApiKey, byLabel };
};

export const applyColumnMappingToRows = (
    rows: TObj[],
    mappingByApiKey: Record<string, string>,
    importTitles: TImportTitles,
): TObj[] => {
    const entries = Object.entries(mappingByApiKey);
    if (entries.length === 0) return rows;

    return rows.map((row) => {
        const next: TObj = { ...row };
        for (const [apiKey, fileColumn] of entries) {
            if (fileColumn === "NotMandatory") {
                delete next[apiKey];
                const label = importTitles[apiKey];
                if (label) delete next[label];
                continue;
            }
            const value = row[fileColumn] ?? "";
            next[apiKey] = value;
            next[importTitles[apiKey]] = value;
        }
        return next;
    });
};
